import React, { ChangeEvent, FunctionComponent, useState } from "react";
import { connect, MapStateToProps } from "react-redux";
import { AutoSizer, CellMeasurerCacheParams, List, ListRowProps } from "react-virtualized";

import { ButtonBackgroundColor, Divider, Icon, IconColor, IconTextButton, InfiniteVirtualizedList, Layout, SpacingSize, VerticalDivider } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames, createSkeletonRowsKeys } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState, KeyGeneratorType, RowRenderer } from "@multiweb/types/react";
import { LOC_EDIT_DEFINITION_PARTICIPANTS, LOC_NO_ITEMS_FOUND, LOC_SEARCH } from "../../../../locKeys";
import { IParticipant, WorkflowEditorProps } from "../../../../types";
import { DividerPosition, ToolbarSection } from "../../../toolbarPanel/components";
import { AddableListFrameItem } from "../../components";
import { DefinitionParticipantsEditDialog } from "../DefinitionParticipantsEditDialog";
import { ParticipantsProvider } from "../participantsProvider";

type ParticipantsTabProps = {
    readonly selectedParticipants: readonly IParticipant[];
    readonly updateParticipants: (newParticipants: readonly IParticipant[]) => void;
    readonly editDefinitionParticipantsText: string;
    readonly searchText: string;
    readonly noItemsText: string;
} & WorkflowEditorProps;

const classNames = bemClassNames("participants-tab-component");
const frame = classNames();
const infiniteListClass = classNames("infinite-list");
const buttonsContainer = classNames("buttons-container");
const participantItemsContainer = classNames("participant-items-container");
const participantsFrame = classNames("participants-frame");
const searchInputClass = classNames("search-input");
const noRowsMessageClass = classNames("no-rows-message");

// Konstanty pro InfiniteVirtualizedList
const ROW_HEIGHT = 34;
const NUMBER_OF_ITEMS_TO_LOAD = 50;
const CELL_MEASURE_CACHE_PARAMS: CellMeasurerCacheParams = { fixedHeight: true, defaultHeight: ROW_HEIGHT, fixedWidth: true };
const createSkeletonRowsKeysObjects = (count: number) => createSkeletonRowsKeys(count).map((identifier) => ({ identifier } as IParticipant));
const keyGenerator: KeyGeneratorType<IParticipant> = (item) => item.identifier;

export const ParticipantsTabComponent: FunctionComponent<ParticipantsTabProps> = ({ hostKey, selectedParticipants, updateParticipants, editDefinitionParticipantsText, searchText, noItemsText }) => {
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);

    const onAddClick = (newParticipant: IParticipant) => {
        updateParticipants([...selectedParticipants, newParticipant]);
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setKeyword(e.target.value);
        setScrollToIndex(0);
    };

    const onEditClick = () => {
        setShowEditDialog(true);
    };

    const onCloseEdit = () => {
        setShowEditDialog(false);
    };

    const onRemoveClick = (participantIndex: number) => {
        const newParticipants = selectedParticipants.filter((_participant, index) => index !== participantIndex);
        updateParticipants(newParticipants);
    };

    const onScroll = () => {
        setScrollToIndex(undefined);
    };

    const itemRenderer: RowRenderer<IParticipant> = (participant: IParticipant): JSX.Element => (
        <AddableListFrameItem onActionButtonClick={() => onAddClick(participant)} style={{ height: ROW_HEIGHT }} showAdd {...{ hostKey, participant }} />
    );

    const rowRenderer = ({ index, key, style }: ListRowProps): JSX.Element => (
        <AddableListFrameItem onActionButtonClick={() => onRemoveClick(index)} participant={selectedParticipants[index]} showAdd={false} {...{ style, hostKey, key }} />
    );

    const noRowsRenderer = (): JSX.Element => (
        <div className={noRowsMessageClass}>{noItemsText}</div>
    );

    return (
        <div className={frame}>
            <div className={buttonsContainer}>
                <ToolbarSection dividerPosition={DividerPosition.none}>
                    <IconTextButton title={editDefinitionParticipantsText} outline={false} value={editDefinitionParticipantsText} iconCode={IconCode.edit} onClick={onEditClick} backgroundColor={ButtonBackgroundColor.transparent} />
                </ToolbarSection>
                <Layout>
                    <ToolbarSection dividerPosition={DividerPosition.left}>
                        <Layout spacingSizeHorizontal={SpacingSize.xs}>
                            <Icon iconCode={IconCode.uiSearch} color={IconColor.grey1} />
                            <input className={searchInputClass} value={keyword} onChange={onInputChange} placeholder={searchText} />
                        </Layout>
                    </ToolbarSection>
                </Layout>
            </div>
            <Divider />
            <div className={participantItemsContainer}>
                <div className={participantsFrame}>
                    <ParticipantsProvider count={NUMBER_OF_ITEMS_TO_LOAD} {...{ selectedParticipants, hostKey, keyword }}>
                        {({ items, canLoadNextItems, fetchingState, loadNextItems }) => (
                            <InfiniteVirtualizedList
                                dataCountToLoad={NUMBER_OF_ITEMS_TO_LOAD}
                                cellMeasureCacheParams={CELL_MEASURE_CACHE_PARAMS}
                                createSkeletonRowsKeys={createSkeletonRowsKeysObjects}
                                {...{ items, fetchingState, scrollToIndex, itemRenderer, noRowsRenderer, canLoadNextItems, loadNextItems, onScroll, keyGenerator }}
                            />)}
                    </ParticipantsProvider>
                </div>
                <VerticalDivider />
                <div className={participantsFrame}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <List
                                className={infiniteListClass}
                                rowCount={selectedParticipants.length}
                                rowHeight={ROW_HEIGHT}
                                {...{ rowRenderer, height, width }}
                            />
                        )}
                    </AutoSizer>
                </div>
            </div>
            {showEditDialog && <DefinitionParticipantsEditDialog {...{ hostKey, onCloseEdit }} />}
        </div>
    );
};

type StateProps = Pick<ParticipantsTabProps, "editDefinitionParticipantsText" | "searchText" | "noItemsText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state) => ({
    editDefinitionParticipantsText: getPrimaryText(state, LOC_EDIT_DEFINITION_PARTICIPANTS) ?? "Editovat definiční hráče",
    searchText: getPrimaryText(state, LOC_SEARCH) ?? "Vyhledat...",
    noItemsText: getPrimaryText(state, LOC_NO_ITEMS_FOUND) ?? "K použitému filtru se nepodařilo najít žádný záznam.",
});

export const ParticipantsTab = connect(mapStateToProps)(ParticipantsTabComponent);
